import { Component, OnDestroy } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AxiosError } from 'axios';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { CLAIMS_FORM_NAME, DEBTOR_FORM_NAME, HU_DEBTOR_FORM_NAME } from 'src/app/services/form/form.factory';
import { FormService, InvoiceOcr } from 'src/app/services/form/form.service';
import { CompanySearchDetailedItem, CompanySearchService } from 'src/app/shared/company-search-service/company-search.service';

@Component({
  selector: 'app-invoice',
  templateUrl: './invoice.component.html',
  styleUrls: ['./invoice.component.scss']
})
export class InvoiceComponent implements OnDestroy {
  private readonly destroy = new Subject<void>();
  readonly file = new FormControl(null, Validators.required);
  loading = false;

  get isGuest(): boolean { return this.formService.caseId === 'guest'; }

  constructor(
    private formService: FormService,
    private companySearchService: CompanySearchService,
  ) {
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  openIntercomWidget(): void {
    const intercom = window?.Intercom as any;
    if (intercom) {
      intercom('showMessages');
    }
  }

  async fileDropped(files: FileList) {
    const file = files.item(0);
    if (file) {
      this.file.setValue(file);
      this.file.markAsDirty();

      await this.setFormByFile(file);
    }
  }

  private async setFormByFile(file: File): Promise<void> {
    this.loading = true;

    const invoiceData = await this.getInvoiceOcr(file);

    const jurisdiction = this.getDebtorForm().get('jurisdiction');
    if (!invoiceData?.debtor_country_iso || invoiceData.debtor_country_iso === jurisdiction.value) {
      await this.updateForm(
        file,
        invoiceData,
      );
      return;
    }

    const timeout = setTimeout(async () => {
      this.destroy.next();
      await this.updateForm(
        file,
        invoiceData,
      );
    }, 10_000);

    this.formService.formChange
      .pipe(
        take(1),
        takeUntil(this.destroy)
      )
      .subscribe(async () => {
        clearTimeout(timeout);
        await this.updateForm(
          file,
          invoiceData,
        );
      });

    jurisdiction.patchValue(invoiceData.debtor_country_iso);
  }

  private async getCompanyData(search?: string): Promise<null | CompanySearchDetailedItem> {
    if (!search || search.length < 3) {
      return null;
    }

    try {
      this.loading = true;
      const result = await this.companySearchService.detailedSearch(search);

      return result?.company;
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error while getting debtor company', error.response?.data);
      } else {
        console.error('Unknown error while getting debtor company', error);
      }
    } finally {
      this.loading = false;
    }
  }

  private async getInvoiceOcr(file: File): Promise<null | InvoiceOcr> {
    try {
      this.loading = true;

      return await this.formService.getInvoiceOcr(file);
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error while getting invoice ocr', error.response?.data);
      } else {
        console.error('Unknown error while getting invoice ocr', error);
      }
    } finally {
      this.loading = false;
    }
  }

  private async updateForm(
    file: File,
    invoiceData?: InvoiceOcr,
  ): Promise<void> {
    this.formService.forceInvoiceOcr.next(true);

    this.patchClaimForm(file, invoiceData);

    try {
      await Promise.all([
        await this.patchDebtorForm(invoiceData),
        await this.patchClientForm(invoiceData),
      ]);
    } catch (error: unknown) {
      console.error('Error while patching form by invoice', error);
    }

    this.formService.forceInvoiceOcr.next(false);
    this.formService.form.updateValueAndValidity();
  }

  private patchClaimForm(file: File, invoiceData?: InvoiceOcr): void {
    const claim = this.getClaimForm();
    this.formService.patchClaimByInvoiceOcr(
      claim,
      invoiceData,
      file,
    );
  }

  private async patchDebtorForm(invoiceData?: InvoiceOcr): Promise<void> {
    const companyData = await this.getCompanyData(invoiceData?.debtor_tax_number || invoiceData?.debtor_name);

    const debtor = this.getDebtorForm();
    debtor.patchValue({
      name: companyData?.basicData.name ?? invoiceData?.debtor_name,
      tax_number: companyData?.basicData?.taxNumber ?? invoiceData?.debtor_tax_number,
      debtor_type: companyData ? 'org' : (invoiceData?.debtor_type ?? 'org'),
      party_type_id: companyData?.basicData.partyType?.internal_id,
      registration_number: companyData?.basicData.registrationNumber,
      email: companyData?.contactData.value,
      address: {
        country_iso: invoiceData?.debtor_country_iso,
        postcode: companyData?.addressData.postcode ?? invoiceData?.debtor_postcode,
        settlement: companyData?.addressData.settlement ?? invoiceData?.debtor_city,
        street: companyData?.addressData.street ?? invoiceData?.debtor_street,
      },
    });
  }

  private async patchClientForm(invoiceData?: InvoiceOcr): Promise<void> {
    if (this.formService.caseId !== 'guest') {
      return;
    }

    const companyData = await this.getCompanyData(invoiceData?.client_tax_number || invoiceData?.client_name);

    await this.formService.upsertBankAccount({
      id: (new Date()).valueOf().toString(),
      account_holder_name: companyData?.bankAccountData?.account_holder_name ?? '',
      account_number: companyData?.bankAccountData?.account_number ?? '',
      iban: companyData?.bankAccountData?.iban ?? '',
    });

    await this.formService.updateClient({
      id: (new Date()).valueOf().toString(),
      name: companyData?.basicData.name ?? invoiceData?.client_name,
      tax_number: companyData?.basicData?.taxNumber ?? invoiceData?.client_tax_number,
      client_type: companyData ? 'org' : (invoiceData?.client_type ?? 'org'),
      party_type_id: companyData?.basicData.partyType?.internal_id,
      registration_number: companyData?.basicData.registrationNumber,
      address: {
        country_iso: invoiceData?.client_country_iso,
        postcode: companyData?.addressData.postcode ?? invoiceData?.client_postcode,
        settlement: companyData?.addressData.settlement ?? invoiceData?.client_city,
        street: companyData?.addressData.street ?? invoiceData?.client_street,
      },
    });
  }

  private getClaimForm(): FormGroup {
    const claims = this.formService.form.get(CLAIMS_FORM_NAME) as FormArray;
    return claims.at(0) as FormGroup;
  }

  private getDebtorForm(): AbstractControl {
    return this.formService.form.get(DEBTOR_FORM_NAME) || this.formService.form.get(HU_DEBTOR_FORM_NAME);
  }
}
